<template>
  <mds-dialog
    :visible="visible"
    :title="translate('dialog.delete.title')"
    class="feed-manager-dialog"
    action-required
  >
    <mds-loader
      v-if="isLoadingData && tableType === FEEDS_TAB"
      size="small"
      class="feed-manager-dialog__loader"
    />
    <div v-else class="feed-manager-dialog__content">
      <div class="feed-manager-dialog__description">
        {{ description }}
      </div>
      <mds-list-group class="feed-manager-dialog__list" v-if="hasSchedules">
        <mds-list-group-item
          v-for="{ id, name } in list"
          :key="id"
          :text="name"
          class="feed-manager-dialog__option"
        />
      </mds-list-group>

      <div class="feed-manager-dialog__checkbox" v-if="!isFeedRunning">
        <input type="checkbox" id="checkbox" v-model="checked" />
        <label for="checkbox">{{ checkboxLabel }}</label>
      </div>

      <div class="feed-manager-dialog__checkbox" v-else>
        <mds-inline-message variation="warning">
          {{ translate('dialog.feed.isRunning') }}
        </mds-inline-message>
      </div>
    </div>

    <template #mds-dialog-actions-right>
      <mds-button-container right-aligned class="feed-manager-dialog__buttons">
        <mds-button
          type="button"
          variation="secondary"
          :text="translate('dialog.delete.actions.cancel')"
          @click.prevent="$emit('close')"
        />
        <mds-button
          type="button"
          variation="primary"
          :text="confirmButtonLabel"
          :disabled="!checked"
          :loading="processing"
          @click.prevent="confirm"
        />
      </mds-button-container>
    </template>
  </mds-dialog>
</template>
<script>
import MdsDialog from '@mds/dialog'
import MdsLoader from '@mds/loader'
import MdsInlineMessage from '@mds/inline-message'
import { MdsButton, MdsButtonContainer } from '@mds/button'
import { MdsListGroup, MdsListGroupItem } from '@mds/list-group'
import MwcBaseHelpersMixin from '@morningstar/mcl-utilities/src/vue/mixins/mwc-base-mixin'
import isEmpty from 'lodash/isEmpty'
import defaultConfig from '@/config/i18n'
import { FEEDS_TAB, SCHEDULES_TAB } from '@/config/tabs'

export default {
  name: 'ConfirmDialog',
  components: {
    MdsDialog,
    MdsLoader,
    MdsButton,
    MdsButtonContainer,
    MdsListGroup,
    MdsListGroupItem,
    MdsInlineMessage,
  },
  mixins: [MwcBaseHelpersMixin],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    tableType: {
      type: String,
      default: FEEDS_TAB,
    },
    list: {
      type: Array,
      default: () => [],
    },
    isFeedRunning: {
      type: Boolean,
      default: undefined,
    },
  },
  data() {
    return {
      defaultConfig,
      processing: false,
      checked: false,
      FEEDS_TAB,
      isLoadingData: undefined,
    }
  },
  created() {
    this.isLoadingData = true
  },
  watch: {
    isFeedRunning: {
      immediate: true,
      deep: true,
      async handler() {
        this.isLoadingData = false
      },
    },
  },
  computed: {
    hasSchedules() {
      return this.tableType === FEEDS_TAB && !isEmpty(this.list)
    },
    description() {
      if (this.tableType === SCHEDULES_TAB) {
        return this.translate('dialog.schedule.delete')
      }

      return this.hasSchedules
        ? this.translate('dialog.feed.schedules.delete')
        : this.translate('dialog.feed.delete')
    },
    checkboxLabel() {
      return this.hasSchedules
        ? this.translate('dialog.feed.schedules.confirm.delete')
        : this.translate('dialog.confirm.delete')
    },
    confirmButtonLabel() {
      if (this.tableType === 'schedules') {
        return this.translate('dialog.schedules.button.delete')
      }

      if (this.hasSchedules) {
        if (this.list.length > 1) {
          return this.translate('dialog.feed.schedules.button.delete')
        } else {
          return this.translate('dialog.feed.schedule.button.delete')
        }
      }
      return this.translate('dialog.feed.button.delete')
    },
  },
  methods: {
    confirm() {
      this.processing = true
      if (this.tableType === FEEDS_TAB) {
        this.$emit('confirmDeleteFeed')
      } else {
        this.$emit('confirmDeleteSchedule')
      }
    },
  },
}
</script>
<style lang="scss" scoped>
@import '@mds/constants';

.feed-manager-dialog {
  &__loader {
    margin: $mds-space-3-x auto;
  }

  &__description {
    margin-bottom: $mds-space-1-and-a-half-x;
  }

  &__buttons {
    gap: $mds-space-1-x;
  }

  &__checkbox {
    margin: $mds-space-2-x 0;
    & > * {
      cursor: pointer;
    }
  }

  &__option {
    position: relative;
    padding-left: $mds-space-2-x;

    &::before {
      content: '\2022';
      color: $mds-color-neutral-80;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      margin: auto 0;
    }
  }
}
</style>
